import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeft,
  Save,
  Sparkles,
  Send,
  Loader2,
  ChevronRight,
  ChevronDown,
  FileText,
  MessageSquare,
  Download,
  MoreVertical,
  Check,
  X,
  Eye,
  Edit3,
} from 'lucide-react';
import { Button, Card, Dropdown } from '../components/design-system';
import { getTemplateById } from '../data/planTemplates';

export const PlanEditorPage = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const chatEndRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  const [plan, setPlan] = useState(null);
  const [sections, setSections] = useState([]);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [viewMode, setViewMode] = useState('edit'); // 'edit' or 'preview'
  
  // Use a ref to track the latest sections for immediate access
  const sectionsRef = useRef([]);

  useEffect(() => {
    fetchPlan();
  }, [planId]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Autosave with debounce
  useEffect(() => {
    if (!activeSection || saved) return;
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      saveSection(activeSection, editingContent, true);
    }, 1500); // Autosave after 1.5s of inactivity
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [editingContent, activeSection, saved]);

  const fetchPlan = async () => {
    try {
      const response = await fetch(`/api/plans/${planId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('omniapps_access_token')}`,
        },
      });

      if (!response.ok) {
        navigate('/plans');
        return;
      }

      const data = await response.json();
      setPlan(data.plan);
      const loadedSections = data.sections || [];
      setSections(loadedSections);
      sectionsRef.current = loadedSections; // Update ref immediately
      setChatMessages(data.conversations || []);

      const tmpl = getTemplateById(data.plan.template_id);
      setTemplate(tmpl);

      // Default to first section
      if (data.sections?.length > 0) {
        const firstSection = data.sections[0];
        setActiveSection(firstSection.section_key);
        setEditingContent(firstSection.content || '');
      } else if (tmpl?.sections?.length > 0) {
        // If no sections exist yet, default to first template section
        setActiveSection(tmpl.sections[0].key);
        setEditingContent('');
      }
    } catch (error) {
      console.error('Failed to fetch plan:', error);
      navigate('/plans');
    } finally {
      setLoading(false);
    }
  };

  // Keep sectionsRef in sync with sections state
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  const handleSectionSelect = async (sectionKey) => {
    if (sectionKey === activeSection) return;
    
    // Save current section before switching (if there are unsaved changes)
    if (activeSection && !saved) {
      await saveSection(activeSection, editingContent, false);
    }
    
    // Find the section content from ref (always has latest value)
    const section = sectionsRef.current.find(s => s.section_key === sectionKey);
    setActiveSection(sectionKey);
    setEditingContent(section?.content || '');
    setSaved(true);
  };

  const saveSection = async (sectionKey, content, isAutosave = false) => {
    if (!isAutosave) setSaving(true);
    try {
      const response = await fetch(`/api/plans/${planId}/sections/${sectionKey}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('omniapps_access_token')}`,
        },
        body: JSON.stringify({ content }),
      });

      if (response.ok) {
        // Update sections state and ref - add if not exists, update if exists
        const exists = sectionsRef.current.some(s => s.section_key === sectionKey);
        let newSections;
        if (exists) {
          newSections = sectionsRef.current.map(s => s.section_key === sectionKey ? { ...s, content } : s);
        } else {
          newSections = [...sectionsRef.current, { section_key: sectionKey, content }];
        }
        // Update ref immediately, then state
        sectionsRef.current = newSections;
        setSections(newSections);
        setSaved(true);
      }
    } catch (error) {
      console.error('Failed to save section:', error);
    } finally {
      if (!isAutosave) setSaving(false);
    }
  };

  const handleContentChange = (e) => {
    setEditingContent(e.target.value);
    setSaved(false);
  };

  const handleSave = async () => {
    if (activeSection) {
      await saveSection(activeSection, editingContent, false);
    }
  };

  const handleRegenerateSection = async (sectionKey) => {
    setSaving(true);
    try {
      const response = await fetch(`/api/plans/${planId}/sections/${sectionKey}/regenerate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('omniapps_access_token')}`,
        },
      });

      if (response.ok) {
        const { content } = await response.json();
        // Update sections ref and state - add if not exists, update if exists
        const exists = sectionsRef.current.some(s => s.section_key === sectionKey);
        let newSections;
        if (exists) {
          newSections = sectionsRef.current.map(s => s.section_key === sectionKey ? { ...s, content } : s);
        } else {
          newSections = [...sectionsRef.current, { section_key: sectionKey, content }];
        }
        // Update ref immediately, then state
        sectionsRef.current = newSections;
        setSections(newSections);
        if (activeSection === sectionKey) {
          setEditingContent(content);
        }
        // AI regeneration already saved to DB, so mark as saved
        setSaved(true);
      }
    } catch (error) {
      console.error('Failed to regenerate section:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSendChat = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const response = await fetch(`/api/plans/${planId}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('omniapps_access_token')}`,
        },
        body: JSON.stringify({
          message: userMessage,
          section: activeSection,
        }),
      });

      if (response.ok) {
        const { reply, updatedContent } = await response.json();
        setChatMessages(prev => [...prev, { role: 'assistant', content: reply }]);
        
        // If AI updated the section content, refresh it
        if (updatedContent && activeSection) {
          setEditingContent(updatedContent);
          setSections(prev => prev.map(s => 
            s.section_key === activeSection ? { ...s, content: updatedContent } : s
          ));
        }
      }
    } catch (error) {
      console.error('Failed to send chat:', error);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: t('chat.errorMessage') 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await fetch(`/api/plans/${planId}/export?format=${format}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('omniapps_access_token')}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${plan.title}.${format}`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to export:', error);
    }
  };

  const updatePlanStatus = async (status) => {
    try {
      await fetch(`/api/plans/${planId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('omniapps_access_token')}`,
        },
        body: JSON.stringify({ status }),
      });
      setPlan(prev => ({ ...prev, status }));
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!plan || !template) {
    return null;
  }

  const activeTemplateSection = template.sections.find(s => s.key === activeSection);

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/plans')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{plan.title}</h1>
              <p className="text-sm text-gray-500">{t(template.nameKey)}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Dropdown
              trigger={
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  {t('editor.export')}
                </Button>
              }
              items={[
                { label: t('editor.exportMarkdown'), onClick: () => handleExport('md') },
                { label: t('editor.exportPdf'), onClick: () => handleExport('pdf') },
              ]}
            />
            
            <Dropdown
              trigger={
                <Button variant="outline" size="sm">
                  {t(`plans.status.${plan.status}`)}
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              }
              items={[
                { label: t('plans.status.draft'), onClick: () => updatePlanStatus('draft') },
                { label: t('plans.status.in_progress'), onClick: () => updatePlanStatus('in_progress') },
                { label: t('plans.status.completed'), onClick: () => updatePlanStatus('completed') },
              ]}
            />
            
            <Button onClick={handleSave} disabled={saving || saved}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : saved ? (
                <Check className="h-4 w-4 mr-2 text-green-500" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {saved ? t('common.saved') : t('common.save')}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sections Sidebar */}
        <div className="w-64 bg-gray-50 border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            <h2 className="text-sm font-medium text-gray-700 mb-3">{t('editor.sections')}</h2>
            <div className="space-y-1">
              {template.sections.map((section) => {
                const planSection = sections.find(s => s.section_key === section.key);
                const hasContent = planSection?.content?.trim().length > 0;
                
                return (
                  <button
                    key={section.key}
                    onClick={() => handleSectionSelect(section.key)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                      activeSection === section.key
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {hasContent ? (
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="truncate">{t(section.titleKey)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {activeTemplateSection && (
            <>
              {/* Section Header */}
              <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
                <h3 className="font-medium text-gray-900">
                  {t(activeTemplateSection.titleKey)}
                </h3>
                <div className="flex items-center gap-2">
                  {/* View Mode Toggle */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('edit')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        viewMode === 'edit'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Edit3 className="h-4 w-4" />
                      {t('editor.edit')}
                    </button>
                    <button
                      onClick={() => setViewMode('preview')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        viewMode === 'preview'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Eye className="h-4 w-4" />
                      {t('editor.preview')}
                    </button>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRegenerateSection(activeSection)}
                    disabled={saving}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {t('editor.regenerate')}
                  </Button>
                </div>
              </div>

              {/* Editor/Preview Area */}
              <div className="flex-1 overflow-y-auto p-6">
                {viewMode === 'edit' ? (
                  <textarea
                    value={editingContent}
                    onChange={handleContentChange}
                    className="w-full h-full min-h-[400px] p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none font-mono text-sm"
                    placeholder={t('editor.startWriting')}
                  />
                ) : (
                  <div className="prose prose-sm max-w-none p-4 bg-white border border-gray-200 rounded-lg min-h-[400px]">
                    {editingContent ? (
                      <ReactMarkdown>{editingContent}</ReactMarkdown>
                    ) : (
                      <p className="text-gray-400 italic">{t('editor.noContent')}</p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* AI Chat Panel */}
        <div className={`bg-white border-l border-gray-200 transition-all duration-300 ${showChat ? 'w-96' : 'w-0'} overflow-hidden`}>
          {showChat && (
            <div className="h-full flex flex-col">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary-600" />
                  <h3 className="font-medium text-gray-900">{t('chat.title')}</h3>
                </div>
                <button
                  onClick={() => setShowChat(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {chatMessages.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    <Sparkles className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">{t('chat.empty')}</p>
                  </div>
                )}
                {chatMessages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-2 ${
                        msg.role === 'user'
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-lg px-4 py-2">
                      <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Chat Input */}
              <div className="p-4 border-t border-gray-200">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
                    placeholder={t('chat.placeholder')}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                  <Button onClick={handleSendChat} disabled={chatLoading || !chatInput.trim()}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Chat Toggle Button */}
        {!showChat && (
          <button
            onClick={() => setShowChat(true)}
            className="fixed right-6 bottom-6 p-4 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-colors"
          >
            <MessageSquare className="h-6 w-6" />
          </button>
        )}
      </div>
    </div>
  );
};
